import { Link, HttpLinkTemplate } from '@lib2/model/link/LinkTypes';
import { LINK_TEMPLATE_TOKENS } from '@/lib/constants/link';
import type { SharedLinkConfig } from '@/lib/types/link';

/**
 * Build URL from template with token replacement.
 * Returns Link instance (use .urlWithQueryString() for final URL).
 */
function buildLink(
  templateLink: Link,
  replacements: Record<string, string | number>
): Link {
  let url = templateLink.data.url;
  for (const [token, value] of Object.entries(replacements)) {
    url = url.replaceAll(token, String(value));
  }
  return new Link({ url, queryString: templateLink.data.queryString });
}

/**
 * Get URL template based on download flag.
 */
function getTemplate(
  linkTemplate: HttpLinkTemplate,
  download: boolean
): Link | null {
  return download ? linkTemplate.data.httpDLUrl : linkTemplate.data.httpUrl;
}

/**
 * Utility class for building signed CDN URLs from link templates.
 * Use with SharedLinkConfig from useSharedLinkConfig() hook.
 */
export class LinkUtils {
  /**
   * Get URL for account-level file (images, assets).
   */
  static getAccountFileUrl(
    config: SharedLinkConfig,
    fileId: number,
    format: string,
    download = false
  ): string | null {
    const template = getTemplate(config.accountHttpTemplate, download);
    if (!template) return null;

    return buildLink(template, {
      [LINK_TEMPLATE_TOKENS.FILE_ID]: fileId,
      [LINK_TEMPLATE_TOKENS.FORMAT]: format,
    }).urlWithQueryString();
  }

  /**
   * Get URL for media-specific file (thumbnails, posters).
   */
  static getMediaFileUrl(
    config: SharedLinkConfig,
    mediaId: number,
    fileId: number,
    format: string,
    download = false
  ): string | null {
    const template = getTemplate(config.mediaFileRSHttpTemplate, download);
    if (!template) return null;

    return buildLink(template, {
      [LINK_TEMPLATE_TOKENS.MEDIA_ID]: mediaId,
      [LINK_TEMPLATE_TOKENS.FILE_ID]: fileId,
      [LINK_TEMPLATE_TOKENS.FORMAT]: format,
    }).urlWithQueryString();
  }

  /**
   * Get URL for parent account file (reseller logos).
   */
  static getParentAccountFileUrl(
    config: SharedLinkConfig,
    fileId: number,
    format: string,
    download = false
  ): string | null {
    const template = getTemplate(config.parentAccountHttpTemplate, download);
    if (!template) return null;

    return buildLink(template, {
      [LINK_TEMPLATE_TOKENS.FILE_ID]: fileId,
      [LINK_TEMPLATE_TOKENS.FORMAT]: format,
    }).urlWithQueryString();
  }

  /**
   * Get URL for player-specific file (overlay images).
   */
  static getPlayerFileUrl(
    config: SharedLinkConfig,
    playerId: number,
    fileId: number,
    format: string,
    download = false
  ): string | null {
    const template = getTemplate(config.playerHttpTemplate, download);
    if (!template) return null;

    return buildLink(template, {
      [LINK_TEMPLATE_TOKENS.PLAYER_ID]: playerId,
      [LINK_TEMPLATE_TOKENS.FILE_ID]: fileId,
      [LINK_TEMPLATE_TOKENS.FORMAT]: format,
    }).urlWithQueryString();
  }
}
